import * as IoC from 'typescript-ioc';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { FormFieldProps, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { action, observable } from 'mobx';
import { ConnectionGroupFieldKey, connectionGroupFormConfigList } from './connection-group-form.config';
import { ConnectionType, IConnectionDetailsDTO, IConnectionGroupDTO } from '../../../../../../api/connections.dao';
import { EventContext, EventName, IEventDispatcher } from '../../../../../../infrastructure/event-dispatcher';
import { ConnectionGroupsPassportPreviewViewModel } from '../connection-groups-passport-preview/connection-group-passport-preview.viewmodel';
import { PassportConfigurationMode } from '../../configure-passport-display/configure-passport-display.config';


export class ConnectionGroupFormVM {
    private static MAX_CONNECTIONS_IN_GROUP: number = 2;

    private static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'ConnectionGroupFormVM', connectionGroupFormConfigList);
            }
        }();
    }

    @observable
    private readonly formDataCtrl: AbstractFormViewModelController;
    @observable
    private enabledConnections: IConnectionDetailsDTO[] = [];
    @observable
    private disabledConnections: IConnectionDetailsDTO[] = [];
    @observable
    private loading = false;

    constructor(
        public readonly group: IConnectionGroupDTO,
        private readonly connections: IConnectionDetailsDTO[],
        private readonly contextParams: { meetingId?: number; roomId?: number },
        private readonly formSubmitAction: (connectionGroup: IConnectionGroupDTO) => Promise<IConnectionGroupDTO>,
        public readonly deleteConnectionGroup: (groupId: number) => void,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly eventDispatcher: IEventDispatcher = IoC.Container.get(IEventDispatcher),
    ) {
        this.formDataCtrl = ConnectionGroupFormVM.createFormVM(group);
        this.initializeConnectionLists(connections);
        this.registerHandlers();
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get maxEnabledConnections(): number {
        return ConnectionGroupFormVM.MAX_CONNECTIONS_IN_GROUP;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get formVM(): AbstractFormViewModelController {
        return this.formDataCtrl;
    }

    public getFormFieldPropsByKey(key: string): FormFieldProps {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key)
        };
    }

    public getEnabledConnections(): IConnectionDetailsDTO[] {
        return this.enabledConnections;
    }

    public getDisabledConnections(): IConnectionDetailsDTO[] {
        return this.disabledConnections;
    }

    public getConnectionGroupsPassportPreviewViewModel = (): ConnectionGroupsPassportPreviewViewModel => {
        const label = (this.formDataCtrl.getValue(ConnectionGroupFieldKey.LABEL) as string).trim();
        return new ConnectionGroupsPassportPreviewViewModel(this.contextParams, this.enabledConnections, label, PassportConfigurationMode.MeetingConnection);
    };

    @action.bound
    public setEnabledConnections(connections: IConnectionDetailsDTO[]): void {
        this.enabledConnections = connections;
    }

    @action.bound
    public enableConnection(index: number): void {
        const [ connection ] = this.disabledConnections.splice(index, 1);
        this.enabledConnections.push(connection);
    }

    @action.bound
    public disableConnection(index: number): void {
        const [ connection ] = this.enabledConnections.splice(index, 1);
        this.disabledConnections.push(connection);
    }

    @action.bound
    public async submitForm(): Promise<boolean> {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            this.startLoading();
            const result = await this.formSubmitAction(this.toDTO());
            this.stopLoading();
            return !!result;
        }
    }

    public onUnmount(): void {
        this.eventDispatcher.unregisterHandler(EventName.ConnectionCreated, this.onConnectionAddedHandler);
        this.eventDispatcher.unregisterHandler(EventName.ConnectionUpdated, this.onConnectionUpdatedHandler);
        this.eventDispatcher.unregisterHandler(EventName.ConnectionDeleted, this.onConnectionDeletedHandler);
    }

    public isEdit = (): boolean => {
        return !!this.group;
    };

    @action
    private initializeConnectionLists(connections: IConnectionDetailsDTO[]): void {
        this.enabledConnections = this.composeEnabledConnectionsList(connections);
        this.disabledConnections = this.composeDisabledConnectionsList(connections);
    }

    private toDTO(): IConnectionGroupDTO {
        const dto = { ...this.group } as IConnectionGroupDTO;

        dto.label = (this.formDataCtrl.getValue(ConnectionGroupFieldKey.LABEL) as string).trim();
        dto.joinMethods = this.enabledConnections.map((connection, idx) => {
            return {
                joinMethodId: connection.joinMethodId,
                order: idx + 1
            };
        });

        return dto;
    }

    private composeEnabledConnectionsList(connections: IConnectionDetailsDTO[]): IConnectionDetailsDTO[] {
        if (!this.group || !this.group.joinMethods.length) return [];

        return this.group.joinMethods
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(({ joinMethodId }) => {
                return connections.find(c => c.joinMethodId === joinMethodId);
            })
            .filter(Boolean)
        ;
    }

    private composeDisabledConnectionsList(connections: IConnectionDetailsDTO[]): IConnectionDetailsDTO[] {
        if (!this.group || !this.group.joinMethods.length) return [ ...connections ];

        return connections.filter(({ joinMethodId }) => {
            return !this.group.joinMethods.find(c => c.joinMethodId === joinMethodId);
        });
    }

    private isContextValid(context: EventContext): boolean {
        return (
            context.meetingId && context.meetingId === this.contextParams.meetingId ||
            context.roomId && context.roomId === this.contextParams.roomId
        );
    }

    private isMeetingUniqueJoiningLink(connection: IConnectionDetailsDTO, context: EventContext): boolean {
        return connection.type === ConnectionType.UniqueJoiningLink && !!context.meetingId;
    }

    @action.bound
    private onConnectionAddedHandler(connection: IConnectionDetailsDTO, context: EventContext): void {
        if (!this.isContextValid(context) || this.isMeetingUniqueJoiningLink(connection, context)) return;

        this.disabledConnections.push(connection);
    }

    @action.bound
    private onConnectionUpdatedHandler(connection: IConnectionDetailsDTO, context: EventContext): void {
        if (!this.isContextValid(context)) return;

        const idx = this.enabledConnections.concat(this.disabledConnections)
            .findIndex(({ meetingConnectionId }) => connection.meetingConnectionId === meetingConnectionId);

        if (idx === -1) return;

        if (idx < this.enabledConnections.length) {
            this.enabledConnections[idx] = connection;
        } else {
            this.disabledConnections[idx - this.enabledConnections.length] = connection;
        }
    }

    @action.bound
    private onConnectionDeletedHandler(connection: Partial<IConnectionDetailsDTO>, context: EventContext): void {
        if (!this.isContextValid(context)) return;

        const idx = this.enabledConnections.concat(this.disabledConnections)
            .findIndex(({ meetingConnectionId }) => connection.meetingConnectionId === meetingConnectionId);

        if (idx === -1) return;

        if (idx < this.enabledConnections.length) {
            this.enabledConnections.splice(idx, 1);
        } else {
            this.disabledConnections.splice(idx - this.enabledConnections.length, 1);
        }
    }

    private registerHandlers(): void {
        this.eventDispatcher.registerHandler(EventName.ConnectionCreated, this.onConnectionAddedHandler);
        this.eventDispatcher.registerHandler(EventName.ConnectionUpdated, this.onConnectionUpdatedHandler);
        this.eventDispatcher.registerHandler(EventName.ConnectionDeleted, this.onConnectionDeletedHandler);
    }

    @action.bound
    private startLoading(): void {
        this.loading = true;
    }

    @action.bound
    private stopLoading(): void {
        this.loading = false;
    }

}
